/**
 * DAC profiles -- group 09 (high-end amplifier houses that also build converters).
 *
 * Two honesty constraints shaped this group and are worth stating up front.
 *
 * 1. None of these eight manufacturers builds an R-2R, discrete-ladder or NOS
 *    converter, so no entry here is tagged with a ladder architecture and no
 *    entry uses a NOS filter. Mislabelling a delta-sigma design as a ladder to
 *    broaden the architecture spread would put a false fact into the database.
 *    The genuine spread available here is delta-sigma / multibit-delta-sigma,
 *    the two FPGA-driven discrete DSD designs from the Meitner lineage, and one
 *    chip-plus-tube hybrid.
 * 2. Every entry carries confidence 'ESTIMATED'. These are low-volume brands
 *    that publish topology and connectivity but rarely a full electrical
 *    specification; the dynamic-range, THD+N, jitter and output-impedance
 *    figures below are class-typical inferences, not quoted numbers, and each
 *    provenanceNote says exactly which side of that line every value falls on.
 *    Prices are approximate for the same reason -- these are dealer-quoted
 *    products whose list prices move between markets and model years.
 */

import type { DacProfile } from '../../types/hardware';
import {
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  SIMPLE_FILTERS,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_09: DacProfile[] = [
  makeDac({
    mfr: 'Jeff Rowland Design Group',
    model: 'Aeris',
    price: usd(9800, 'APPROXIMATE', 2013, 'Approximate US retail at introduction; Rowland pricing is dealer-quoted and this figure is not a confirmed list price.'),
    year: 2012,
    country: 'United States',
    architecture: 'delta-sigma',
    core: 'Dual-differential delta-sigma conversion behind a fixed-rate DSP resampler; converter device not published',
    outputStage: 'op-amp',
    dynamicRangeDb: 118,
    thdPlusNDb: -105,
    crosstalkDb: -112,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 100,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: SIMPLE_FILTERS(),
    defaultFilterId: 'sharp-linear',
    internalOversampling: 4,
    jitterPsRms: 25,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#8a94a6',
    tags: ['fixed-rate-resampling', 'pcm-only', 'outboard-supply'],
    notes:
      'Every input is resampled to one internal rate before conversion, so the reconstruction filter and its ringing signature stay constant instead of changing with the source rate as they do in a chipset that switches filter families at 44.1k and 48k multiples. The analogue stage is a low-impedance solid-state line output, which keeps the distortion floor essentially level-independent above -60 dBFS -- the opposite of the ladder behaviour modelled elsewhere in the database. Input support stops at 24-bit/192 kHz PCM with no DSD path, so this is the one profile in group 09 that cannot accept a DSD source at all.',
    provenanceNote:
      'Fixed-rate internal resampling, the PCM-only 24-bit/192 kHz input set and the balanced output configuration are recalled from Jeff Rowland published material. The converter device is not published and is described by class only. Dynamic range, THD+N, crosstalk, output impedance, effective bits and jitter are class-typical estimates, not quoted figures. Price is approximate.',
  }),

  makeDac({
    mfr: 'Boulder Amplifiers',
    model: '2120',
    price: usd(55000, 'APPROXIMATE', 2021, 'Approximate US retail; Boulder list pricing for the 2100 series is not consistently published and moves between model years.'),
    year: 2019,
    country: 'United States',
    architecture: 'multibit-delta-sigma',
    core: 'Current-output multibit delta-sigma conversion into Boulder 993 discrete gain stages',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 126,
    thdPlusNDb: -112,
    crosstalkDb: -130,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'dsd128',
    filters: [FILTERS.sharpLinear(256), FILTERS.apodizing(128)],
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 12,
    effectiveBits: 21,
    noiseShaperOrder: 6,
    confidence: 'ESTIMATED',
    accent: '#5f7d95',
    tags: ['balanced-only', 'discrete-gain-stage', 'flagship'],
    notes:
      'The signal path is differential from the converter outputs onward and terminates in discrete Class A gain modules rather than IC op-amps, so what remains of the distortion floor is set by the analogue stage and not by the converter. Because the two halves are summed differentially, even-order products largely cancel and the modelled harmonic structure is odd-weighted despite a Class A topology -- the profile is deliberately different from a single-ended Class A design at the same THD figure. The real unit has no single-ended outputs; the unbalanced level given here exists only so the engine has a value to work with.',
    provenanceNote:
      'Discrete gain-stage output topology and the balanced-only signal path are from Boulder published material. The converter device is not published and is described by class. Dynamic range, THD+N, crosstalk, jitter, noise-shaper order, effective bits and output impedance are estimates typical of this class of design, not quoted specifications. Price is approximate.',
  }),

  makeDac({
    mfr: 'Constellation Audio',
    model: 'Cygnus',
    price: usd(29000, 'APPROXIMATE', 2020, 'Approximate US retail; the Cygnus shipped in several configurations and Constellation pricing is dealer-quoted.'),
    year: 2016,
    country: 'United States',
    architecture: 'delta-sigma',
    core: 'Delta-sigma conversion into a JFET-input balanced analogue stage derived from the Constellation line-stage modules; converter device not published',
    outputStage: 'jfet',
    dynamicRangeDb: 122,
    thdPlusNDb: -108,
    crosstalkDb: -118,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 40,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowMinimum(32), FILTERS.apodizing(128)],
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 20,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#9fb3c8',
    tags: ['jfet-input', 'media-player', 'selectable-filter'],
    notes:
      'A JFET front end has a square-law transfer characteristic, so the residual distortion of the analogue stage is second-harmonic-weighted and grows roughly two dB per dB of drive rather than staying flat -- audibly and measurably unlike the op-amp stage in the Rowland entry at a similar THD number. Three selectable filters are modelled, including a slow minimum-phase option whose entire impulse energy sits after the sample instant, so switching filters here changes pre-ringing from full to essentially none. Output impedance is low enough to make cable and input-loading interaction negligible.',
    provenanceNote:
      'Balanced JFET-based analogue stage lineage from Constellation published descriptions of its line-stage circuitry; applying that lineage to the Cygnus output stage is an inference, not a published statement. The converter device is not published. Dynamic range, THD+N, crosstalk, output impedance, jitter and the filter complement are estimates. Price is approximate.',
  }),

  makeDac({
    mfr: 'Gryphon Audio Designs',
    model: 'Kalliope',
    price: usd(32000, 'APPROXIMATE', 2016, 'Approximate US retail at introduction; Gryphon pricing varies significantly by market.'),
    year: 2015,
    country: 'Denmark',
    architecture: 'multibit-delta-sigma',
    core: 'Paralleled ESS Sabre multibit delta-sigma converters in a dual-mono balanced configuration',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 127,
    thdPlusNDb: -114,
    crosstalkDb: -125,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 20,
    balanced: true,
    parallelUnits: 8,
    dsd: 'dsd256',
    filters: ESS_FILTERS(),
    defaultFilterId: 'sharp-linear',
    jitterPsRms: 15,
    effectiveBits: 21,
    noiseShaperOrder: 5,
    confidence: 'ESTIMATED',
    accent: '#c0392b',
    tags: ['paralleled-converters', 'dual-mono', 'zero-feedback-output'],
    notes:
      'Paralleling eight converter channels per side sums the wanted signal coherently while the uncorrelated converter noise sums as the square root of the count, which is where roughly nine dB of the noise-floor advantage over a single-chip design comes from; the profile therefore carries a very low noise level but an ordinary chip-level linearity. The zero-feedback discrete Class A current-to-voltage and output stages then set the distortion, so the harmonic tail is low-order and rises with level rather than staying at a fixed floor. The full Sabre filter set is exposed, making this the most filter-configurable entry in the group.',
    provenanceNote:
      'Paralleled ESS Sabre conversion, dual-mono layout and the zero-feedback Class A discrete output stage are from Gryphon published descriptions of the Kalliope; the exact number of paralleled converter channels is treated as approximate. Dynamic range, THD+N, crosstalk, output impedance, jitter and noise-shaper order are estimates, not quoted figures. Price is approximate.',
  }),

  makeDac({
    mfr: 'Vitus Audio',
    model: 'MP-D201',
    price: usd(45000, 'APPROXIMATE', 2018, 'Approximate US retail for the Masterpiece series converter; Vitus pricing is dealer-quoted and this figure is not confirmed.'),
    year: 2014,
    country: 'Denmark',
    architecture: 'multibit-delta-sigma',
    core: 'Delta-sigma conversion into a pure Class A discrete analogue output; converter device not published',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 121,
    thdPlusNDb: -104,
    crosstalkDb: -115,
    outputVrmsSE: 2.4,
    outputVrmsBal: 4.8,
    outputImpedanceOhms: 30,
    balanced: true,
    dsd: 'dsd128',
    filters: [FILTERS.sharpMinimum(96), FILTERS.slowMinimum(32)],
    defaultFilterId: 'sharp-min',
    jitterPsRms: 25,
    effectiveBits: 20,
    confidence: 'ESTIMATED',
    accent: '#b9a15a',
    tags: ['class-a', 'minimum-phase-default', 'masterpiece-series'],
    notes:
      'The distinguishing choice modelled here is the default filter: both options are minimum-phase, so the impulse response has no energy ahead of the sample instant and the profile produces zero pre-ringing on band-edge transients, at the cost of frequency-dependent group delay in the top octave. The analogue side is a low-feedback Class A discrete stage running at high bias, which trades an absolute THD figure roughly ten dB worse than the Boulder entry for a distortion tail that stops at the fourth harmonic instead of extending upward. Output impedance sits between the discrete and op-amp designs in this group.',
    provenanceNote:
      'Pure Class A discrete analogue output and the Masterpiece series positioning are from Vitus published material. The converter device is not published. The minimum-phase filter complement, dynamic range, THD+N, crosstalk, output impedance, jitter and effective bits are estimates typical of this class of design, not quoted specifications. Price is approximate.',
  }),

  makeDac({
    mfr: 'EMM Labs',
    model: 'DV2',
    price: usd(30000, 'APPROXIMATE', 2019, 'Approximate US retail at introduction for the DV2 integrated converter.'),
    year: 2018,
    country: 'Canada',
    architecture: 'fpga-custom',
    core: 'FPGA MDAT2 interpolation into a proprietary discrete dual-differential DSD conversion stage with analogue output-level control',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 120,
    thdPlusNDb: -102,
    crosstalkDb: -112,
    outputVrmsSE: 2.4,
    outputVrmsBal: 4.8,
    outputImpedanceOhms: 25,
    balanced: true,
    dsd: 'dsd512',
    filters: [
      { ...FILTERS.apodizing(256), id: 'mdat2', label: 'MDAT2 time-domain interpolation (fixed)', cutoffRatio: 0.9, transitionRatio: 0.12, stopbandDb: -110 },
    ],
    defaultFilterId: 'mdat2',
    internalOversampling: 64,
    jitterPsRms: 10,
    effectiveBits: 20,
    idleToneLevel: 0.12,
    confidence: 'ESTIMATED',
    accent: '#4f6d7a',
    tags: ['discrete-dsd', 'fixed-filter', 'analogue-volume'],
    notes:
      'PCM is interpolated in an FPGA and re-expressed as a high-rate one-bit stream, so PCM and DSD sources converge on the same final conversion hardware and the difference between them collapses to what the interpolator does. There is exactly one reconstruction filter and no user choice, which makes this profile the counter-example to the six-filter Gryphon entry: the time-domain behaviour is fixed by the designer. A one-bit conversion stage has no ladder element mismatch to model, but it does carry a modelled idle-tone tendency that a multibit converter does not.',
    provenanceNote:
      'FPGA-based interpolation into a proprietary discrete DSD conversion stage, the single fixed reconstruction filter and analogue output-level control are from EMM Labs published descriptions. EMM Labs does not publish a THD or THD+N specification for this product, so the distortion figure here is an estimate, as are dynamic range, crosstalk, output impedance, jitter, effective bits and the idle-tone level. Price is approximate.',
  }),

  makeDac({
    mfr: 'Meitner Audio',
    model: 'MA3',
    price: usd(10500, 'APPROXIMATE', 2022, 'Approximate US retail; the MA3 has been repriced since introduction.'),
    year: 2021,
    country: 'Canada',
    architecture: 'fpga-pulse-array',
    core: 'FPGA MDAT2 upsampling driving a one-bit discrete conversion stage, shared design lineage with the EMM Labs converters',
    outputStage: 'op-amp',
    dynamicRangeDb: 116,
    thdPlusNDb: -98,
    crosstalkDb: -104,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 60,
    balanced: true,
    dsd: 'dsd512',
    filters: [
      { ...FILTERS.apodizing(192), id: 'mdat2-ma3', label: 'MDAT2 interpolation (fixed)', cutoffRatio: 0.88, transitionRatio: 0.14, stopbandDb: -100 },
    ],
    defaultFilterId: 'mdat2-ma3',
    internalOversampling: 32,
    jitterPsRms: 18,
    effectiveBits: 19,
    idleToneLevel: 0.18,
    confidence: 'ESTIMATED',
    accent: '#6b8f9e',
    tags: ['discrete-dsd', 'streaming', 'fixed-filter'],
    notes:
      'Same conversion principle as the EMM Labs entry -- FPGA interpolation into a one-bit discrete stage -- but implemented at a third of the price, and the profile is built to show where that money goes rather than to duplicate its sibling. The interpolator is modelled as shorter, the clock as roughly twice as jittery, the noise floor four dB higher and the idle-tone tendency correspondingly greater, and the analogue stage is an integrated-circuit line output with three times the source impedance. Nothing about the reconstruction shape is user-adjustable here either.',
    provenanceNote:
      'FPGA interpolation into a one-bit discrete conversion stage and the shared design lineage with EMM Labs are from Meitner published material. The integrated-circuit output stage is an inference from the price position, not a published statement. Meitner does not publish THD for this product; distortion, dynamic range, crosstalk, output impedance, jitter, interpolator length, effective bits and idle-tone level are all estimates. Price is approximate.',
  }),

  makeDac({
    mfr: 'Audio Research',
    model: 'Reference DAC',
    price: usd(15995, 'APPROXIMATE', 2013, 'Approximate original US retail; long discontinued, so this is a historical figure rather than a current one.'),
    year: 2013,
    country: 'United States',
    architecture: 'hybrid',
    core: 'Chip-based delta-sigma conversion into a vacuum-tube analogue output stage',
    outputStage: 'tube',
    dynamicRangeDb: 108,
    thdPlusNDb: -78,
    crosstalkDb: -85,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 600,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'dsd64',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowLinear(48)],
    defaultFilterId: 'sharp-linear',
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(7, -0.9),
    slewRateVus: 15,
    jitterPsRms: 60,
    effectiveBits: 18,
    confidence: 'ESTIMATED',
    accent: '#c8a24a',
    tags: ['tube-output', 'coupling-capacitor', 'reference-series'],
    notes:
      'The converter is not what this profile is about: the triode output stage dominates every audible axis, contributing a strongly second-harmonic-weighted residue that rises about one dB per dB of level while the converter behind it sits thirty dB lower. A 600 ohm source impedance is high enough to form an audible high-pass with a low-impedance following input, and the output coupling capacitor adds its own low-frequency corner near 7 Hz, both of which the response model applies. Input support ends at 24-bit/192 kHz PCM plus single-rate DSD, so it is the most limited digital front end in the group.',
    provenanceNote:
      'Vacuum-tube analogue output stage, Reference series positioning and the 24-bit/192 kHz era input set are from Audio Research published material; the converter device is not published, and the architecture is tagged hybrid in this database to record that chip conversion feeds a tube stage. Audio Research does not publish a full THD+N figure for this product, so distortion, dynamic range, crosstalk, output impedance, slew rate and jitter are estimates typical of tube-output converters of this generation. Price is approximate and historical.',
  }),
];
